import { Botao } from '../../components/Botao'
import { dataHora } from '../../lib/datas'
import { TEXTOS } from '../../lib/glossario'
import type { AutorNaTela } from '../../types/repositorios'
import { CHIP } from './CelulaDaSolicitacao'
import { EtiquetaDeSetor } from './EtiquetaDeSetor'

const INTEIRO = new Intl.NumberFormat('pt-BR')

interface Props {
  autor: AutorNaTela
  fiscal: boolean
  aoCorrigirSetor: () => void
  aoMudarEquipeInterna: () => void
}

/**
 * Uma pessoa na lista de autores conhecidos.
 *
 * A célula da equipe interna diz POR QUE a pessoa está lá (pelo login, pelo e-mail, por decisão
 * de alguém) e com quem ela casou no Diário: é o que o fiscal precisa para desconfiar de um
 * casamento errado por homônimo. Quem foi TIRADO por uma pessoa também aparece com a origem e o
 * nome de quem decidiu — um "—" igual ao de quem nunca casou apagaria a decisão da tela.
 *
 * @param props o autor, se quem vê é fiscal e as duas ações
 * @returns a linha
 */
export function LinhaDeAutor({ autor, fiscal, aoCorrigirSetor, aoMudarEquipeInterna }: Props) {
  const t = TEXTOS.repositorios
  const ta = t.autores
  const porPessoa = autor.equipeInternaOrigem === 'PESSOA'
  // Com origem PESSOA o backend grava o motivo `PESSOA` também: mostrá-lo repetiria "decisão de
  // uma pessoa · fixado por uma pessoa". O motivo só interessa quando foi o Diário que casou.
  const motivo = !porPessoa && autor.equipeInternaMotivo ? (ta.motivos[autor.equipeInternaMotivo] ?? autor.equipeInternaMotivo) : null
  const quem = autor.equipeInternaDefinidoPor ? ` (${autor.equipeInternaDefinidoPor})` : ''
  // A data só importa quando foi uma pessoa que decidiu: é o carimbo do ato, e não da rodada do Diário.
  const quando = porPessoa && autor.equipeInternaDefinidoEm ? ` · ${dataHora(autor.equipeInternaDefinidoEm)}` : ''

  return (
    <tr className="border-b border-slate-100 align-top last:border-0">
      <td className="px-3 py-2">
        <p className="font-medium text-slate-900">{autor.nomeDeExibicao ?? autor.nome}</p>
        <p className="text-xs text-slate-500">
          {autor.email}
          {autor.login && ` · @${autor.login}`}
        </p>
      </td>
      <td className="px-3 py-2">
        <EtiquetaDeSetor setor={autor.setor} />
        {autor.setorFixadoPorPessoa && <p className="mt-1 text-xs text-niteroi-700">{t.setorFixado}</p>}
      </td>
      <td className="px-3 py-2">
        {autor.equipeInterna ? (
          <>
            <span className={`${CHIP} bg-slate-100 text-slate-700 ring-slate-200`}>{t.equipeInterna.chip}</span>
            <p className="mt-1 text-xs text-slate-500">
              {motivo && `${motivo} · `}
              {porPessoa ? ta.fixadoPorPessoa : ta.peloDiario}
              {quem}
              {quando}
            </p>
            {autor.pessoaDoDiario && <p className="text-xs text-slate-500">{ta.casadaCom(autor.pessoaDoDiario.nome)}</p>}
          </>
        ) : porPessoa ? (
          <p className="text-xs text-slate-500">
            {ta.foraPorPessoa}
            {quem}
            {quando}
          </p>
        ) : (
          <span className="text-slate-400">{ta.naoEh}</span>
        )}
      </td>
      <td className="px-3 py-2 text-right tabular-nums text-slate-700">{INTEIRO.format(autor.commits)}</td>
      <td className="px-3 py-2">
        {fiscal && (
          <div className="flex flex-wrap justify-end gap-2">
            <Botao aparencia="texto" tamanho="pequeno" onClick={aoCorrigirSetor}>
              {t.pessoas.corrigirSetor}
            </Botao>
            <Botao aparencia="texto" tamanho="pequeno" onClick={aoMudarEquipeInterna}>
              {autor.equipeInterna ? ta.desmarcar : ta.marcar}
            </Botao>
          </div>
        )}
      </td>
    </tr>
  )
}
